// 사용자(손님) 인증 세션 store.
// 보안 메모: localStorage 영속은 XSS 노출 위험이 있어 docs/ui-guidelines.md §8 는 액세스 토큰을 메모리 우선으로 권장한다.
// 다만 PWA 새 탭/새 고침 시 로그인 유지가 UX 상 중요하므로 Phase 1 골격에서는 localStorage 영속을 채택하고,
// Phase 5 보안 강화 단계에서 Capacitor Preferences / httpOnly 쿠키 조합으로 이관하는 것을 명시적 TODO 로 둔다.
//
// 토큰 회전 정책: /me 401 응답 시 refresh 시도(1-G) → 실패 시 clearSession 후 /login 으로.

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// 현재 로그인된 사용자 요약 정보.
// 상세는 useMe() 훅으로 서버에서 받아 사용한다 - 본 store 는 식별/등급 등 즉시 노출 정보만 캐시.
export interface AuthUser {
  id: string;
  displayName: string | null;
  tier: string;
}

// 로그인 세션.
// expiresAtSec 은 액세스 토큰의 epoch 만료 시각으로, 클라이언트가 만료 임박 시 사전 갱신에 활용한다.
export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  expiresAtSec: number;
  user: AuthUser;
}

interface AuthStore {
  session: AuthSession | null;
  // 로그인/회원가입 성공 시 호출.
  setSession: (session: AuthSession) => void;
  // 사용자 부분 정보 갱신용 (예: 마이페이지 등급 변경).
  updateUser: (patch: Partial<AuthUser>) => void;
  // 로그아웃/세션 만료 처리.
  clearSession: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      session: null,
      setSession: (session) => set({ session }),
      updateUser: (patch) =>
        set((state) =>
          state.session ? { session: { ...state.session, user: { ...state.session.user, ...patch } } } : state,
        ),
      clearSession: () => set({ session: null }),
    }),
    {
      name: "oo-user-auth",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
